import { z } from "zod";

import { emailTemplateTypeSchema } from "./email";
import { amountSchema } from "./money";

export const orderItemFulfillmentStatusSchema = z
  .enum(["unfulfilled", "partial", "fulfilled", "cancelled"])
  .describe("OrderItemFulfillmentStatus");

// Mirrors the Prisma `OrderFinancialStatus` enum so wire contracts share one
// source of truth instead of hand-copied enum literals.
export const orderFinancialStatusSchema = z
  .enum(["pending", "paid", "failed", "refunded", "partiallyRefunded"])
  .describe("OrderFinancialStatus");

// Snapshot of the variant option selection at the time the order was placed
// (e.g. `[{ name: "Size", value: "Medium" }]`). Mirrored from the Prisma
// triple-slash type `OrderItemOptionValuesSnapshot` so the wire contract stays
// in sync with what the database stores.
export const orderItemOptionValueSchema = z
  .object({
    name: z.string(),
    value: z.string(),
  })
  .describe("OrderItemOptionValue");

export const orderItemOptionValuesSnapshotSchema = z
  .array(orderItemOptionValueSchema)
  .describe("OrderItemOptionValuesSnapshot");

export const orderFulfillmentStatusSchema = z
  .enum([
    "unfulfilled",
    "inProgress",
    "onHold",
    "partiallyFulfilled",
    "fulfilled",
    "requestDeclined",
    "cancelled",
  ])
  .describe("OrderFulfillmentStatus");

export const stripeRefundReasonSchema = z
  .enum([
    "duplicate",
    "fraudulent",
    "requested_by_customer",
    "expired_uncaptured_charge",
  ])
  .describe("StripeRefundReason");

// Who fulfills a FulfillmentOrder. Persisted (required) on FulfillmentLocation.fulfillmentProviderKey; "manual" is explicit. Backend-core routing writes it; bedrock dispatch reads it.
export const fulfillmentProviderKeySchema = z
  .enum(["printful", "digital", "manual"])
  .describe("FulfillmentProviderKey");

// The display name a provider's fulfillment location is created with. Shared
// so every getOrCreate site (publisher routing, bedrock inventory writes)
// creates identically named rows.
export function locationNameFor(
  provider: z.infer<typeof fulfillmentProviderKeySchema>,
): string {
  return provider === "manual" ? "Default" : provider;
}

// The purchase-time record of one ordered line as materialized onto the order
// item row: catalog snapshots + quantity + minor-unit amounts. Emails render
// line items from exactly this view.
export const orderItemSnapshotSchema = z
  .object({
    variantTitleSnapshot: z.string(),
    productTitleSnapshot: z.string(),
    imageUrlSnapshot: z.string().nullable(),
    quantity: z.number().int(),
    unitPrice: amountSchema,
    totalPrice: amountSchema,
  })
  .describe("OrderItemSnapshot");

// The order's shipping-address contract; mirrors the Prisma OrderAddress type and is the shape Printful dispatch reads.
export const orderAddressSchema = z
  .object({
    line1: z.string(),
    line2: z.string().optional(),
    city: z.string(),
    state: z.string().optional(),
    postalCode: z.string(),
    // ISO 3166-1 alpha-2, as written by Stripe checkout and read by Printful dispatch
    country: z.string().regex(/^[A-Z]{2}$/),
    name: z.string().optional(),
    phone: z.string().optional(),
  })
  .describe("OrderAddress");

export const orderCustomerInfoSchema = z
  .object({
    name: z.string().optional(),
    phone: z.string().optional(),
  })
  .catch({})
  .describe("OrderCustomerInfo");

export const orderEventActorSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("user"),
      userId: z.uuid(),
    }),
    z.object({
      type: z.literal("agent"),
      userId: z.uuid(),
      agentSessionId: z.string(),
    }),
    z.object({
      type: z.literal("system"),
    }),
    z.object({
      type: z.literal("provider"),
      agentIntegrationConnectionId: z.uuid(),
      providerKey: z.string(),
    }),
  ])
  .describe("OrderEventActor");

// Named so the public API contract can re-derive these two variants without
// their Stripe identifiers; every other variant is shared as-is.
export const orderEventPaymentCapturedMetadataSchema = z.object({
  kind: z.literal("paymentCaptured"),
  amount: amountSchema,
  currencyCode: z.string(),
  stripePaymentIntentId: z.string(),
});

export const orderEventPaymentFailedMetadataSchema = z.object({
  kind: z.literal("paymentFailed"),
  source: z.enum(["payment_intent.payment_failed", "charge.failed"]),
  stripePaymentIntentId: z.string().optional(),
  stripeChargeId: z.string().optional(),
  stripeErrorCode: z.string().optional(),
  stripeDeclineCode: z.string().optional(),
  message: z.string().optional(),
});

export const orderEventMetadataSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("note"),
      markdown: z.string(),
    }),
    orderEventPaymentCapturedMetadataSchema,
    orderEventPaymentFailedMetadataSchema,
    z.object({
      kind: z.literal("refundIssued"),
      amount: amountSchema,
      currencyCode: z.string(),
      reason: stripeRefundReasonSchema.nullish(),
      isFullRefund: z.boolean(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderCreated"),
      fulfillmentOrderId: z.uuid(),
      itemCount: z.number(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderSubmitted"),
      fulfillmentOrderId: z.uuid(),
      providerKey: z.string(),
      externalRef: z.string(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderAccepted"),
      fulfillmentOrderId: z.uuid(),
      providerKey: z.string(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderRejected"),
      fulfillmentOrderId: z.uuid(),
      providerKey: z.string(),
      reason: z.string(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderHeld"),
      fulfillmentOrderId: z.uuid(),
      reason: z.string(),
      reasonNotes: z.string().optional(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderReleased"),
      fulfillmentOrderId: z.uuid(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderIncomplete"),
      fulfillmentOrderId: z.uuid(),
      reason: z.string(),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderCancelled"),
      fulfillmentOrderId: z.uuid(),
      cancelledBy: z.enum(["merchant", "provider", "system"]),
    }),
    z.object({
      kind: z.literal("fulfillmentOrderCancellationRequested"),
      fulfillmentOrderId: z.uuid(),
    }),
    z.object({
      kind: z.literal("shipmentCreated"),
      fulfillmentOrderId: z.uuid(),
      fulfillmentId: z.uuid(),
    }),
    z.object({
      kind: z.literal("shipmentSent"),
      fulfillmentOrderId: z.uuid(),
      fulfillmentId: z.uuid(),
      trackingCarrier: z.string().optional(),
      trackingNumber: z.string().optional(),
      trackingUrl: z.string().optional(),
    }),
    z.object({
      kind: z.literal("shipmentDelivered"),
      fulfillmentOrderId: z.uuid(),
      fulfillmentId: z.uuid(),
    }),
    z.object({
      kind: z.literal("shipmentReturned"),
      fulfillmentOrderId: z.uuid(),
      fulfillmentId: z.uuid(),
      reason: z.string().optional(),
    }),
    z.object({
      kind: z.literal("shipmentCancelled"),
      fulfillmentOrderId: z.uuid(),
      fulfillmentId: z.uuid(),
    }),
    z.object({
      kind: z.literal("inventoryDecremented"),
      entries: z.array(
        z.object({
          variantId: z.uuid(),
          locationId: z.uuid(),
          delta: z.number(),
          before: z.number(),
          after: z.number(),
        }),
      ),
    }),
    // Buyer-email delivery claim — same §4.3 lifecycle as
    // merchantNotificationSent below. `templateType` names the
    // merchant-authored email.
    z.discriminatedUnion("status", [
      z.object({
        kind: z.literal("customerEmailSent"),
        templateType: emailTemplateTypeSchema,
        status: z.literal("pending"),
      }),
      z.object({
        kind: z.literal("customerEmailSent"),
        templateType: emailTemplateTypeSchema,
        status: z.literal("sent"),
        to: z.string(),
        resendMessageId: z.string().optional(),
      }),
      z.object({
        kind: z.literal("customerEmailSent"),
        templateType: emailTemplateTypeSchema,
        status: z.literal("failed"),
        to: z.string().optional(),
      }),
      z.object({
        kind: z.literal("customerEmailSent"),
        templateType: emailTemplateTypeSchema,
        status: z.literal("skipped"),
        reason: z.enum(["noCustomerEmail"]),
      }),
    ]),
    // Delivery claim (§4.3): the row is inserted `pending` before the send and
    // settled afterward, so the timeline never claims an email that wasn't
    // sent. Discriminated on status so each state carries exactly its fields —
    // a claim is taken before recipient resolution (pending has no `to`), a
    // skip means no recipient exists, and a settled send always names one.
    z.discriminatedUnion("status", [
      z.object({
        kind: z.literal("merchantNotificationSent"),
        status: z.literal("pending"),
      }),
      z.object({
        kind: z.literal("merchantNotificationSent"),
        status: z.literal("sent"),
        to: z.string(),
        resendMessageId: z.string(),
      }),
      z.object({
        kind: z.literal("merchantNotificationSent"),
        status: z.literal("failed"),
        to: z.string(),
      }),
      z.object({
        kind: z.literal("merchantNotificationSent"),
        status: z.literal("skipped"),
      }),
    ]),
  ])
  .describe("OrderEventMetadata");
